<template>
  <div class="stats-container m-t-lg d-f fd-c">
    <div class="stats-total-section border d-f fd-c m p-xl ai-c tt-up g br-lg">
      <p class="">Total</p>
      <div class="stats-total-cards d-f w-100 g">
        <div class="stats-card d-f fd-c p-lg card-bg br-lg flex-1 g">
          <p class="h1">{{ totalPlays }}</p>
          <p class="mute font-small d-f ai-c g-half">
            <IconBase :w="12" :h="12" :color="colors['textColorMute']">
              <IconPlay />
            </IconBase>
            Total Plays
          </p>
        </div>
        <div class="stats-card d-f fd-c p-lg card-bg br-lg flex-1 g">
          <p class="h1">{{ totalLikes }}</p>
          <p class="mute font-small d-f ai-c g-half">
            <IconBase :w="12" :h="12" :color="colors['textColorMute']">
              <IconHeart />
            </IconBase>
            Total Likes
          </p>
        </div>
        <div class="stats-card d-f fd-c p-lg card-bg br-lg flex-1 g">
          <p class="h1">{{ totalComments }}</p>
          <p class="mute font-small d-f ai-c g-half">
            <IconBase :w="12" :h="12" :color="colors['textColorMute']">
              <IconMessage />
            </IconBase>
            Total Comments
          </p>
        </div>
        <div class="stats-card d-f fd-c p-lg card-bg br-lg flex-1 g">
          <p class="h1">{{ totalPlaylistAdded }}</p>
          <p class="mute font-small d-f ai-c g-half">
            <IconBase :w="12" :h="12" :color="colors['textColorMute']">
              <IconList />
            </IconBase>
            Total Playlist Adds
          </p>
        </div>
        <div class="stats-card d-f fd-c p-lg card-bg br-lg flex-1 g">
          <p class="h1">{{ totalUniquePlays }}</p>
          <p class="mute font-small d-f ai-c g-half">
            <IconBase :w="12" :h="12" :color="colors['textColorMute']">
              <IconUser2 />
            </IconBase>
            Total Unique Plays
          </p>
        </div>
      </div>
    </div>

    <div
      class="stats-artist-ranking-section border d-f fd-c m p-xl ai-c g br-lg"
    >
      <p class="tt-up">Artist Ranking</p>
      <p class="mute tt-up ta-c">
        BASED ON ENGAGEMENT SCORE
        <span class="white">{{ engagementScore }}</span> ( TOTAL INTERACTIONS
        <span class="white">{{ totalInteractions }}</span> ÷ UNIQUE PLAYS
        <span class="white">{{ totalUniquePlays }}</span
        >)
      </p>

      <div
        v-if="artistLocRanksLoading || !userSettings"
        class="stats-loading d-f jc-c g w-100 ai-s"
      >
        <p class="h1 mute text-outline">Loading</p>
      </div>
      <div
        v-else-if="artistLocRanks.length > 0"
        v-for="(r, i) in artistLocRanks"
        :key="r.loc + i"
        class="stats-ranking-list d-f fd-c g w-100 ai-s"
      >
        <div class="stats-ranking-card d-f p-lg card-bg br-lg">
          <div class="flex-1">
            <p class="h3">{{ r.loc }}</p>
            <p>
              #{{ r.rank
              }}<span class="mute">
                out of {{ r.totalCount }} artist{{
                  typeof r.totalCount === "string" || r.totalCount > 1
                    ? "s"
                    : ""
                }}</span
              >
            </p>
          </div>
          <p class="h1"><span class="mute">#</span>{{ r.rank }}</p>
        </div>
      </div>
      <div v-else class="stats-no-data d-f jc-c g w-100 ai-s">
        <p class="h1 mute text-outline">No Data</p>
      </div>
    </div>

    <div
      class="stats-track-ranking-section border d-f fd-c m p-xl ai-c g br-lg"
    >
      <p class="tt-up">Track Ranking</p>

      <div
        v-if="trackRankingTabs.length > 0"
        class="stats-tabs-container b-bottom w-100 ji-c tabs-container embedded-tabs-container"
      >
        <div class="stats-tabs d-f g tt-up w-fc">
          <div
            v-for="(tab, i) in trackRankingTabs"
            :key="tab + i"
            class="flex-1 ta-c p-t p-b tab d-f fd-c"
            :class="selectedTrackRankingTab === i ? 'selected' : ''"
            @click="selectedTrackRankingTab = i"
          >
            {{ tab }}
          </div>
        </div>
      </div>

      <div
        v-if="trackLocRanksLoading || !userSettings"
        class="stats-loading d-f jc-c g w-100 ai-s"
      >
        <p class="h1 mute text-outline">Loading</p>
      </div>
      <div
        v-else-if="trackLocRanksToShow.ranks.length > 0"
        v-for="(r, i) in trackLocRanksToShow.ranks"
        :key="r.songId + i"
        class="stats-ranking-list d-f fd-c g w-100 ai-s"
      >
        <div class="stats-ranking-card d-f p-lg card-bg br-lg">
          <div class="flex-1">
            <p class="h3">{{ userMusic[r.songId]?.title ?? "" }}</p>
            <p>
              <router-link
                :to="
                  '/music/geo-' +
                  encodeURIComponent(trackRankingTabs[selectedTrackRankingTab])
                "
                >#{{ r.rank }}
                {{ trackRankingTabs[selectedTrackRankingTab] }}</router-link
              >
              <span class="mute">
                out of {{ trackLocRanksToShow.totalCount }} track{{
                  trackLocRanksToShow.totalCount > 1 ? "s" : ""
                }}</span
              >
            </p>
          </div>
          <p class="h1"><span class="mute">#</span>{{ r.rank }}</p>
        </div>
      </div>
      <div v-else class="stats-no-data d-f jc-c g w-100 ai-s">
        <p class="h1 mute text-outline">No Data</p>
      </div>
    </div>

    <div
      class="stats-genre-ranking-section border d-f fd-c m p-xl ai-c g br-lg"
    >
      <p class="tt-up">Genre Ranking</p>

      <div
        v-if="genreRankingTabs.length > 0"
        class="stats-tabs-container b-bottom w-100 ji-c tabs-container embedded-tabs-container"
      >
        <div class="stats-tabs d-f g tt-up w-fc">
          <div
            v-for="(tab, i) in genreRankingTabs"
            :key="tab + i"
            class="flex-1 ta-c p-t p-b tab d-f fd-c"
            :class="selectedGenreRankingTab === i ? 'selected' : ''"
            @click="selectedGenreRankingTab = i"
          >
            {{ tab }}
          </div>
        </div>
      </div>

      <div
        v-if="trackGenRanksLoading || !userSettings"
        class="stats-loading d-f jc-c g w-100 ai-s"
      >
        <p class="h1 mute text-outline">Loading</p>
      </div>
      <div
        v-else-if="trackGenRanksToShow.ranks.length > 0"
        v-for="(r, i) in trackGenRanksToShow.ranks"
        :key="r.songId + i"
        class="stats-ranking-list d-f fd-c g w-100 ai-s"
      >
        <div class="stats-ranking-card d-f p-lg card-bg br-lg">
          <div class="flex-1">
            <p class="h3">{{ userMusic[r.songId]?.title ?? "" }}</p>
            <p>
              <router-link
                :to="
                  '/music/genre-' +
                  encodeURIComponent(genreRankingTabs[selectedGenreRankingTab])
                "
                >#{{ r.rank }}
                {{ genreRankingTabs[selectedGenreRankingTab] }}</router-link
              >
              <span class="mute">
                out of {{ trackGenRanksToShow.totalCount }} track{{
                  trackGenRanksToShow.totalCount > 1 ? "s" : ""
                }}</span
              >
            </p>
          </div>
          <p class="h1"><span class="mute">#</span>{{ r.rank }}</p>
        </div>
      </div>
      <div v-else class="stats-no-data d-f jc-c g w-100 ai-s">
        <p class="h1 mute text-outline">No Data</p>
      </div>
    </div>

    <div
      class="stats-track-performance-section border d-f fd-c m p-xl ai-c g br-lg"
    >
      <p class="tt-up">Track Performance</p>

      <div class="stats-table-wrapper">
        <div class="stats-table">
          <div class="stats-table-header">
            <div class="stats-table-cell">Song title</div>
            <div class="stats-table-cell">Plays</div>
            <div class="stats-table-cell">Likes</div>
            <div class="stats-table-cell">Added to Playlist</div>
            <div class="stats-table-cell">Comments</div>
            <div class="stats-table-cell">Total Interactions</div>
            <div class="stats-table-cell">Unique Plays</div>
            <div class="stats-table-cell">Engagement Score</div>
          </div>
          <div
            v-for="(data, songId) in userMusic"
            :key="songId"
            class="stats-table-row"
          >
            <div class="stats-table-cell" data-label="Song title">
              {{ data.title }}
            </div>
            <div class="stats-table-cell" data-label="Plays">
              {{ getSongInteractions(songId).plays }}
            </div>
            <div class="stats-table-cell" data-label="Likes">
              {{ getSongInteractions(songId).likes }}
            </div>
            <div class="stats-table-cell" data-label="Added to Playlist">
              {{ getSongInteractions(songId).addedToPlaylists }}
            </div>
            <div class="stats-table-cell" data-label="Comments">
              {{ getSongInteractions(songId).comments }}
            </div>
            <div class="stats-table-cell" data-label="Total Interactions">
              {{ getSongInteractions(songId).totalInteractions }}
            </div>
            <div class="stats-table-cell" data-label="Unique Plays">
              {{ getSongInteractions(songId).uniquePlays }}
            </div>
            <div class="stats-table-cell" data-label="Engagement Score">
              {{ getSongInteractions(songId).engagementScore }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import IconPlay from "@/assets/icons/IconPlay.vue";
import IconUser2 from "@/assets/icons/IconUser2.vue";
import IconBase from "@/assets/icons/IconBase.vue";
import colors from "@/scss/_variables.scss";
import IconHeart from "@/assets/icons/IconHeart.vue";
import IconMessage from "@/assets/icons/IconMessage.vue";
import IconList from "@/assets/icons/IconList.vue";
import { rtdb } from "@/db";
import { mapGetters, mapState } from "vuex";
import { getShallowDataFromFirebaseDb } from "@/common";
import Footer from "@/components/navigation/Footer.vue";

export default {
  name: "Stats",
  components: {
    Footer,
    IconList,
    IconMessage,
    IconHeart,
    IconBase,
    IconPlay,
    IconUser2,
  },
  mounted() {
    this.loadData();
    this.loadArtistLocRanks();
    this.loadTrackLocRanks();
    this.loadTrackGenRanks();
  },
  data() {
    return {
      colors,
      selectedTrackRankingTab: 0,
      selectedGenreRankingTab: 0,
      loading: false,
      userMusic: {},
      allInteractions: {},
      artistLocRanks: [],
      artistLocRanksLoading: false,
      trackLocRanks: [],
      trackLocRanksLoading: false,
      trackGenRanks: [],
      trackGenRanksLoading: false,
    };
  },
  methods: {
    loadData() {
      // Load all music of user
      rtdb.ref(`userMusic/${this.myUid}`).once("value", (snapshot) => {
        // New users have no node — val() is null; Object.keys(null) throws in computeds/loaders
        this.userMusic = snapshot.val() ?? {};
      });

      // Load all interactions on this user's content
      rtdb.ref(`interactions/${this.myUid}`).once("value", (snapshot) => {
        this.allInteractions = snapshot.val() ?? {};
      });
    },
    loadArtistLocRanks() {
      if (this.artistLocRanksLoading || this.artistLocRanks.length > 0) {
        return;
      }

      this.artistLocRanksLoading = true;

      // Load all users profile ranks
      const rankLocationsRef = rtdb.ref(`rankLocations/rankProfiles/allUsers`);
      Promise.all(
        this.locations.map((l) =>
          Promise.all([
            rankLocationsRef
              .child(l)
              .orderByChild("key")
              .equalTo(this.myUid)
              .get()
              .then((snapshot) => {
                let val = {};
                snapshot.forEach(function (data) {
                  const rank = parseInt(data.key) + 1;
                  val = {
                    loc: l,
                    rank: rank,
                  };
                });
                return val;
              }),
            getShallowDataFromFirebaseDb(
              `/rankLocations/rankProfiles/allUsers/${l}`
            ),
          ]).then(([rank, allLocRanks]) => {
            if (typeof rank?.loc !== "string") {
              rank = {
                rank: 1,
                loc: l,
              };
            }
            rank.totalCount =
              Object.keys(allLocRanks ?? { a: 0 }).length || "many";
            return rank;
          })
        )
      )
        .then((ranks) => {
          this.artistLocRanks = ranks;
        })
        .finally(() => {
          this.artistLocRanksLoading = false;
        });
    },
    loadTrackLocRanks() {
      if (this.trackLocRanksLoading || this.trackLocRanks.length > 0) {
        return;
      }

      this.trackLocRanksLoading = true;

      // Load all tracks location ranks
      const rankLocationsRef = rtdb.ref(`rankLocations/rankSongs/allUsers`);

      Promise.all(
        this.locations.map((l) =>
          Promise.all([
            Promise.all(
              Object.keys(this.userMusic ?? {}).map((songId) =>
                rankLocationsRef
                  .child(l)
                  .orderByChild("key")
                  .equalTo(songId)
                  .get()
                  .then((snapshot) => {
                    let val = {};
                    snapshot.forEach(function (data) {
                      const rank = parseInt(data.key) + 1;
                      val = {
                        songId,
                        rank,
                      };
                    });
                    return val;
                  })
              )
            ),
            getShallowDataFromFirebaseDb(
              `/rankLocations/rankSongs/allUsers/${l}`
            ),
          ]).then(([ranks, allLocRanks]) => {
            ranks = ranks
              .filter((r) => r.songId)
              .toSorted((a, b) => a.rank - b.rank);

            return {
              ranks,
              totalCount: Object.keys(allLocRanks ?? { a: 0 }).length || "many",
            };
          })
        )
      )
        .then((ranks) => {
          this.trackLocRanks = ranks;
        })
        .finally(() => {
          this.trackLocRanksLoading = false;
        });
    },
    loadTrackGenRanks() {
      if (this.trackGenRanksLoading || this.trackGenRanks.length > 0) {
        return;
      }

      this.trackGenRanksLoading = true;

      // Load add track genre ranks
      const genreTracksRef = rtdb.ref(`rankGenres/rankSongs/allUsers`);

      Promise.all(
        this.myGenres.map((g) =>
          Promise.all([
            Promise.all(
              Object.keys(this.userMusic).map((songId) =>
                genreTracksRef
                  .child(g)
                  .orderByChild("key")
                  .equalTo(songId)
                  .get()
                  .then((snapshot) => {
                    let val = {};
                    snapshot.forEach(function (data) {
                      const rank = parseInt(data.key) + 1;
                      val = {
                        songId,
                        rank,
                      };
                    });
                    return val;
                  })
              )
            ),
            getShallowDataFromFirebaseDb(`/rankGenres/rankSongs/allUsers/${g}`),
          ]).then(([ranks, allGenRanks]) => {
            ranks = ranks
              .filter((r) => r.songId)
              .toSorted((a, b) => a.rank - b.rank);

            return {
              ranks,
              totalCount: Object.keys(allGenRanks ?? { a: 0 }).length || "many",
            };
          })
        )
      )
        .then((ranks) => {
          this.trackGenRanks = ranks;
        })
        .finally(() => {
          this.trackGenRanksLoading = false;
        });
    },

    getSongInteractions(songId) {
      const obj = {
        plays: this.interactions[songId]?.plays ?? 0,
        likes: (this.interactions[songId]?.like ?? 0) % 2,
        addedToPlaylists: this.interactions[songId]?.addedToPlaylists ?? 0,
        comments: this.interactions[songId]?.comments ?? 0,
        uniquePlays: this.interactions[songId]?.uniquePlays ?? 0,
      };
      obj.totalInteractions = obj.likes + obj.addedToPlaylists + obj.comments;

      const score = obj.totalInteractions / obj.uniquePlays;
      obj.engagementScore = isNaN(score) ? "0" : score.toFixed(2);

      return obj;
    },
  },
  computed: {
    ...mapState(["userSettings"]),
    ...mapGetters(["myUid"]),

    locations() {
      return Object.values(this.userSettings?.locations ?? {}).toReversed();
    },

    trackRankingTabs() {
      return this.locations;
    },

    myGenres() {
      return Array.from(
        new Set(
          Object.values(this.userMusic ?? {})
            .map((s) => Object.values(s.genres ?? {}))
            .flat()
        )
      );
    },

    genreRankingTabs() {
      return this.myGenres;
    },

    interactions() {
      return Object.fromEntries(
        Object.keys(this.userMusic ?? {}).map((songId) => [
          songId,
          Object.entries(this.allInteractions?.[songId] ?? {})
            .map(([key, data]) => (key === this.myUid ? null : data))
            .filter(Boolean) // Filter out own interactions
            .reduce(
              (acc, cur) => {
                acc.like += (cur.like ?? 0) % 2;
                acc.plays += cur.plays ?? 0;
                acc.addedToPlaylists += cur.playlist ? 1 : 0;
                acc.comments += cur.comments ? 1 : 0;
                if ((cur.plays ?? 0) > 0) {
                  acc.uniquePlays += 1;
                }
                return acc;
              },
              {
                like: 0,
                plays: 0,
                uniquePlays: 0,
                addedToPlaylists: 0,
                comments: 0,
              }
            ),
        ])
      );
    },

    totalPlays() {
      return Object.values(this.interactions).reduce(
        (acc, cur) => acc + cur.plays,
        0
      );
    },

    totalLikes() {
      return Object.values(this.interactions).reduce(
        (acc, cur) => acc + cur.like,
        0
      );
    },

    totalComments() {
      return Object.values(this.interactions).reduce(
        (acc, cur) => acc + cur.comments,
        0
      );
    },

    totalPlaylistAdded() {
      return Object.values(this.interactions).reduce(
        (acc, cur) => acc + cur.addedToPlaylists,
        0
      );
    },

    totalInteractions() {
      return this.totalLikes + this.totalComments + this.totalPlaylistAdded;
    },

    totalUniquePlays() {
      return Object.values(this.interactions).reduce(
        (acc, cur) => acc + cur.uniquePlays,
        0
      );
    },

    engagementScore() {
      const score = this.totalInteractions / this.totalUniquePlays;
      return isNaN(score) ? "0" : score.toFixed(2);
    },

    trackLocRanksToShow() {
      return (
        this.trackLocRanks[this.selectedTrackRankingTab] ?? {
          ranks: [],
          totalCount: 0,
        }
      );
    },

    trackGenRanksToShow() {
      return (
        this.trackGenRanks[this.selectedGenreRankingTab] ?? {
          ranks: [],
          totalCount: 0,
        }
      );
    },
  },
  watch: {
    userSettings() {
      this.loadArtistLocRanks();
      this.loadTrackLocRanks();
      this.loadTrackGenRanks();
    },
    userMusic: {
      deep: true,
      handler() {
        this.loadTrackLocRanks();
        this.loadTrackGenRanks();
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/scss/_variables.scss";
@import "@/scss/_mixins.scss";

// ============================================
// Base Table Styles (CSS Grid)
// ============================================
.stats-table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.stats-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  width: 100%;
  gap: 0;
}

.stats-table-header {
  display: contents;

  .stats-table-cell {
    color: $text-color-mute;
    font-size: $p1-small;
    padding: 6px;
    border-bottom: 1px solid $line-color;
    text-align: left;
  }
}

.stats-table-row {
  display: contents;

  .stats-table-cell {
    padding: 6px;
    border-bottom: 1px solid $line-color;
    text-align: left;
  }
}

// ============================================
// Mobile Responsive Styles
// ============================================
@media #{$mobile} {
  // --- Container ---
  .stats-container {
    margin-top: $mg-desktop-lg;
  }

  // --- Total Stats Section ---
  .stats-total-section {
    padding: $mg-desktop !important;
    margin: $mg-desktop-sm !important;
  }

  .stats-total-cards {
    flex-wrap: wrap;
    gap: $mg-desktop-sm;

    .stats-card {
      flex: 1 1 calc(50% - #{$mg-desktop-sm / 2});
      min-width: calc(50% - #{$mg-desktop-sm / 2});
      padding: $mg-desktop-sm !important;
    }
  }

  // --- Ranking Sections (Artist, Track, Genre) ---
  .stats-artist-ranking-section,
  .stats-track-ranking-section,
  .stats-genre-ranking-section,
  .stats-track-performance-section {
    padding: $mg-desktop !important;
    margin: $mg-desktop-sm !important;
  }

  // --- Ranking Cards ---
  .stats-ranking-card {
    align-items: flex-start !important;
    gap: $mg-desktop-sm;
    padding: $mg-desktop-sm !important;

    .h1 {
      // align-self: flex-end;
      margin-top: $mg-desktop-sm;
    }
  }

  // --- Tabs Container ---
  .stats-tabs-container {
    .stats-tabs {
      flex-wrap: nowrap;
      min-width: max-content;
    }
  }

  // --- Table ---
  .stats-table-wrapper {
    margin: 0 -$mg-desktop-sm;
    padding: 0 $mg-desktop-sm;
  }

  .stats-table {
    min-width: 600px; // Ensure table is wide enough to scroll
    grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;

    .stats-table-cell {
      padding: $mg-desktop-sm;
      font-size: $p1-small;
      white-space: nowrap;
    }
  }

  // --- Typography ---
  .h2 {
    font-size: $h2-m;
  }

  .h3 {
    font-size: $h3-m;
  }

  // --- Spacing ---
  .g {
    gap: $mg-desktop-sm;
  }
}
</style>
